import React, { useState, useEffect, useRef } from "react";
import { database } from "../firebaseConfig";
import { ref, onValue, remove, set, get } from "firebase/database";
import WhisperUploader from "./WhisperUploader";
import WhisperAnalytics from "./WhisperAnalytics";
import "./WhisperSettings.css";

const WhisperSettings = ({ userId }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [profileNumber, setProfileNumber] = useState(0);
  const [savingSettings, setSavingSettings] = useState(false);
  const [copySuccess, setCopySuccess] = useState("");
  const [showDatabasePopup, setShowDatabasePopup] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [smartAddEnabled, setSmartAddEnabled] = useState(false);
  const databasePathRef = useRef(null);
  
  // Database URL and path for this user
  const databaseURL = "https://bonusclub-ab5e2-default-rtdb.europe-west1.firebasedatabase.app";
  const databasePath = `/Whisper/${userId}`;
  const fullDatabasePath = `${databaseURL}${databasePath}`;
  
  // Function to copy user ID to clipboard
  const copyUserId = () => {
    if (databasePathRef.current) {
      databasePathRef.current.select();
      document.execCommand('copy');
      setCopySuccess("User ID copied to clipboard!");
      setTimeout(() => setCopySuccess(""), 3000);
    }
  };
  
  // Toggle database popup
  const toggleDatabasePopup = () => {
    setShowDatabasePopup(!showDatabasePopup);
    setCopySuccess("");
  };
  
  // Close database popup when clicked outside
  const handlePopupOutsideClick = (e) => {
    if (e.target.classList.contains('whisper-settings-popup-overlay')) {
      setShowDatabasePopup(false);
      setCopySuccess("");
    }
  };
  
  // Load existing Instagram profiles
  useEffect(() => {
    if (!userId) return;

    const profilesRef = ref(database, `/Whisper/${userId}/db/profiles`);
    const unsubscribe = onValue(profilesRef, (snapshot) => {
      if (snapshot.exists()) {
        const profilesData = [];
        snapshot.forEach((childSnapshot) => {
          profilesData.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setProfiles(profilesData);
      } else {
        setProfiles([]);
      }
      setLoading(false);
    });

    // Load settings
    const loadSettings = async () => {
      try {
        const settingsRef = ref(database, `/Whisper/${userId}/db/settings`);
        const snapshot = await get(settingsRef);
        
        if (snapshot.exists()) {
          const settings = snapshot.val();
          if (settings.profileNumber !== undefined) {
            setProfileNumber(settings.profileNumber);
          }
          if (settings.smartAddEnabled !== undefined) {
            setSmartAddEnabled(settings.smartAddEnabled);
          }
        }
      } catch (error) {
        console.error("Error loading settings:", error);
      }
    };
    
    loadSettings();
    
    return () => unsubscribe();
  }, [userId]);

  // Function to save settings
  const handleSaveSettings = async () => {
    if (!userId) return;
    
    try {
      setSavingSettings(true);
      
      // Save settings
      const settingsRef = ref(database, `/Whisper/${userId}/db/settings`);
      await set(settingsRef, {
        profileNumber: profileNumber,
        smartAddEnabled: smartAddEnabled
      });
      
      setMessage("Settings saved successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error saving settings:", error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setSavingSettings(false);
    }
  };

  // Function to delete a profile
  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm("Are you sure you want to delete this profile?")) return;

    try {
      const profileRef = ref(database, `/Whisper/${userId}/db/profiles/${profileId}`);
      await remove(profileRef);
      setMessage("Profile deleted successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting profile:", error);
      setMessage(`Error: ${error.message}`);
    }
  };

  // Function to delete all profiles
  const handleDeleteAllProfiles = async () => {
    if (!window.confirm("WARNING: This will delete ALL Instagram profiles. This action cannot be undone. Are you sure?")) return;
    
    // Double confirmation for critical action
    if (!window.confirm("Please confirm again that you want to delete ALL profiles.")) return;
    
    try {
      setDeletingAll(true);
      
      // Delete all profiles
      const profilesRef = ref(database, `/Whisper/${userId}/db/profiles`);
      await remove(profilesRef);
      
      setMessage("All profiles have been deleted successfully!");
      setTimeout(() => setMessage(""), 5000);
    } catch (error) {
      console.error("Error deleting all profiles:", error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setDeletingAll(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Function to open Instagram profile
  const openInstagramProfile = (profileUrl) => {
    window.open(profileUrl, '_blank', 'noopener,noreferrer');
  };

  // Function to standardize profile link if old format is used
  const getProfileUrl = (profile) => {
    // If new format is used, return the URL directly
    if (profile.profile_url) {
      return profile.profile_url;
    }
    
    // Otherwise build the URL from the profile name
    return `https://instagram.com/${profile.instagram_profile}`;
  };

  // Function to toggle Smart Send
  const toggleSmartAdd = () => {
    setSmartAddEnabled(!smartAddEnabled);
  };

  return (
    <div className="whisper-settings-container">
      <h3 className="whisper-settings-title">Instagram Settings</h3>
      
      {/* Database Path Button */}
      <div className="whisper-settings-database-button-container">
        <button 
          className="whisper-settings-database-button"
          onClick={toggleDatabasePopup}
          title="Show user ID"
        >
          <span className="whisper-settings-database-icon">🔑</span>
          User ID
        </button>
      </div>
      
      {/* Database Path Popup */}
      {showDatabasePopup && (
        <div 
          className="whisper-settings-popup-overlay"
          onClick={handlePopupOutsideClick}
        >
          <div className="whisper-settings-popup">
            <div className="whisper-settings-popup-header">
              <h4>User ID</h4>
              <button 
                className="whisper-settings-popup-close" 
                onClick={toggleDatabasePopup}
              >
                ×
              </button>
            </div>
            <div className="whisper-settings-popup-content">
              <p className="whisper-settings-popup-description">
                Your Instagram User ID:
              </p>
              <div className="whisper-settings-path-container">
                <input 
                  type="text" 
                  ref={databasePathRef}
                  value={userId}
                  readOnly
                  className="whisper-settings-path-input"
                />
                <button 
                  onClick={copyUserId}
                  className="whisper-settings-copy-button"
                >
                  Copy
                </button>
              </div>
              {copySuccess && <div className="whisper-settings-copy-success">{copySuccess}</div>}
            </div>
          </div>
        </div>
      )}
      
      {/* WhisperAnalytics component */}
      <WhisperAnalytics userId={userId} />
      
      {/* General Settings */}
      <div className="whisper-settings-section">
        <h4 className="whisper-settings-subtitle">General Settings</h4>
        
        <div className="whisper-settings-field">
          <label htmlFor="profileNumber">Profile Number:</label>
          <div className="whisper-settings-profile-input-container">
            <input
              type="number"
              id="profileNumber"
              value={profileNumber}
              onChange={(e) => setProfileNumber(parseInt(e.target.value) || 0)}
              min="0"
              className="whisper-settings-profile-number-input"
            />
          </div>
          <p className="whisper-settings-help-text">
            This number is used for Instagram profiles and can be freely chosen.
          </p>
        </div>

        <div className="whisper-settings-field">
          <label htmlFor="smartSend">Smart Send:</label>
          <div className="whisper-smart-toggle-wrapper">
            <input
              type="checkbox"
              id="smartSend"
              checked={smartAddEnabled}
              onChange={toggleSmartAdd}
              className="whisper-smart-checkbox"
            />
            <label
              htmlFor="smartSend"
              className="whisper-smart-toggle"
            >
              <span className="whisper-smart-slider"></span>
            </label>
            <span className="whisper-smart-status">
              {smartAddEnabled ? "On" : "Off"}
            </span>
          </div>
          <p className="whisper-settings-help-text">
            Enable Smart Send to automatically add new profiles.
          </p>
        </div>

        <div className="whisper-settings-button-container">
          <button 
            className="whisper-settings-save-button"
            onClick={handleSaveSettings}
            disabled={savingSettings}
          >
            {savingSettings ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
      
      {/* WhisperUploader component */}
      <div className="whisper-settings-section">
        <WhisperUploader userId={userId} />
      </div>
      
      {/* Display existing profiles */}
      <div className="whisper-settings-profiles-section">
        <h4 className="whisper-settings-subtitle">Existing Instagram Profiles</h4>
        
        {loading ? (
          <p>Loading profiles...</p>
        ) : profiles.length === 0 ? (
          <p>No Instagram profiles available. Please import profiles using the CSV upload.</p>
        ) : (
          <>
            <div className="whisper-settings-profiles-info">
              <p>There are {profiles.length} Instagram profiles available.</p>
              
              {/* Delete All Profiles Button */}
              <div className="whisper-settings-delete-all-container">
                <button 
                  className="whisper-settings-delete-all-button"
                  onClick={handleDeleteAllProfiles}
                  disabled={deletingAll || profiles.length === 0}
                >
                  {deletingAll ? "Deleting..." : "Delete All Entries"}
                </button>
              </div>
            </div>
            
            <div className="whisper-settings-table-container">
              <table className="whisper-settings-table">
                <thead>
                  <tr>
                    <th>Instagram Profile</th>
                    <th>Link</th>
                    <th>Status</th>
                    <th>Account No.</th>
                    <th>Imported on</th>
                    <th>Source</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((profile) => (
                    <tr key={profile.id}>
                      <td>
                        {profile.instagram_profile}
                        {profile.raw_value && profile.raw_value !== profile.instagram_profile && (
                          <span className="whisper-settings-original-value" title={`Original: ${profile.raw_value}`}>
                            *
                          </span>
                        )}
                      </td>
                      <td>
                        <a 
                          href={getProfileUrl(profile)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="whisper-settings-instagram-link"
                        >
                          Open
                        </a>
                      </td>
                      <td>
                        <span className={`whisper-settings-status-badge whisper-settings-status-${profile.status || 'unknown'}`}>
                          {profile.status || "Unknown"}
                        </span>
                      </td>
                      <td className="whisper-settings-account-number">
                        {profile.accountNumber !== undefined ? profile.accountNumber : "-"}
                      </td>
                      <td>{formatDate(profile.import_date)}</td>
                      <td>{profile.source || "Unknown"}</td>
                      <td>
                        <button 
                          className="whisper-settings-delete-button"
                          onClick={() => handleDeleteProfile(profile.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        
        {message && <div className="whisper-settings-message">{message}</div>}
      </div>
    </div>
  );
};

export default WhisperSettings; 